import { matchPath } from "react-router-dom";
import { OrderType } from "../../features/orders/orderAPI";
import { PATHS } from "../paths";

export const DEFAULT_DOCUMENT_TITLE = "Eezy Expert";

// App routes these as :tab. PATHS only lists the tabs that have link helpers.
const TALENT_DETAIL = `${PATHS.talents}/:talentId`;
const CUSTOMER_DETAIL = `${PATHS.customers}/:customerCompanyId`;

export type Translate = (key: string) => string;

export type DocumentTitleInput = {
  pathname: string;
  talentName?: string;
  customerName?: string;
  orderType?: string;
  orderCustomerName?: string;
  invoiceCustomerName?: string;
  jobAdTitle?: string;
};

const appendName = (label: string, name?: string) =>
  name ? `${label} - ${name}` : label;

// matchPath leaves %2C encoded. Splitting avoids decodeURIComponent, which throws on a bare %.
const invoiceIdsFromSegment = (segment: string) =>
  segment.split(/%2C|,/i).filter(Boolean);

const resolveInvoiceTitle = (
  invoiceIds: string | undefined,
  input: DocumentTitleInput,
  t: Translate,
) => {
  const openInvoiceCount = invoiceIdsFromSegment(invoiceIds ?? "").length;
  if (openInvoiceCount > 1) return t("documentTitle.multipleInvoices");
  // Keep the static title until the customer name has loaded.
  if (!input.invoiceCustomerName) return undefined;
  return appendName(t("invoices:invoice"), input.invoiceCustomerName);
};

const resolveOrderTitle = (input: DocumentTitleInput, t: Translate) => {
  if (!input.orderType || !input.orderCustomerName) return undefined;
  const orderLabel =
    input.orderType === OrderType.Short
      ? t("documentTitle.shortOrder")
      : t("documentTitle.longOrder");
  return appendName(orderLabel, input.orderCustomerName);
};

const sectionTitles = [
  [PATHS.messaging, "messaging:messaging"],
  [PATHS.entries, "entries"],
  [PATHS.salaries, "payslips"],
  [PATHS.feedbacks, "feedbacks:feedbacks"],
  [PATHS.settings, "settings"],
  [PATHS.talents, "talents"],
  [PATHS.customers, "customers"],
  [PATHS.orders, "orders"],
  [PATHS.invoices, "invoices"],
  [PATHS.jobAds, "jobAds"],
] as const;

export const resolveDocumentTitle = (
  input: DocumentTitleInput,
  t: Translate,
): string | undefined => {
  const { pathname } = input;
  const matched = (pattern: string, end = true) =>
    matchPath({ path: pattern, end }, pathname);

  // Invoices: most specific routes first.
  if (matched(PATHS.invoiceExamine) || matched(PATHS.invoiceExamineRow)) {
    return t("invoices:invoiceAccountingRows");
  }
  if (matched(PATHS.invoiceNew)) return t("documentTitle.createInvoice");

  const openInvoice = matched(PATHS.invoiceEdit) ?? matched(PATHS.invoicePreview);
  if (openInvoice) {
    return resolveInvoiceTitle(openInvoice.params.invoiceIds, input, t);
  }

  // Orders: most specific routes first.
  if (matched(PATHS.orderNew)) return t("documentTitle.createOrder");
  if (matched(PATHS.workOffer)) return t("documentTitle.workOffer");
  if (
    matched(PATHS.orderEdit) ||
    matched(PATHS.orderCopy) ||
    matched(PATHS.workAgreementEdit)
  ) {
    return resolveOrderTitle(input, t);
  }

  // Job ads: "new" before dynamic :jobAdId.
  if (matched(PATHS.jobAdNew)) return t("documentTitle.createJobAd");
  if (matched(PATHS.jobAdView)) {
    return input.jobAdTitle
      ? appendName(t("documentTitle.jobAd"), input.jobAdTitle)
      : undefined;
  }

  // Entity detail routes. Any tab under the id uses the same title.
  // Undefined keeps the static title until the entity name has loaded.
  if (matched(TALENT_DETAIL, false)) return input.talentName;
  if (matched(CUSTOMER_DETAIL, false)) return input.customerName;

  // Section fallbacks, including unknown paths under a known section.
  const sectionTitle = sectionTitles.find(([pattern]) => matched(pattern, false));
  if (sectionTitle) return t(sectionTitle[1]);

  if (matched(PATHS.home)) return t("home");

  return DEFAULT_DOCUMENT_TITLE;
};
